import { API_BASE_URL } from "./constants";

const DEFAULT_TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 60_000;

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

type ParsedApiError = {
  message: string;
  code?: string;
  details?: unknown;
};

export type UploadDriverPictureResponse = {
  url?: string;
  picture?: string;
  data?: {
    url?: string;
    picture?: string;
  };
};

let _onSessionExpired: (() => void) | null = null;
let _sessionExpiredFired = false;

export function setSessionExpiredHandler(handler: () => void) {
  _onSessionExpired = handler;
  _sessionExpiredFired = false;
}

function notifySessionExpired() {
  if (_sessionExpiredFired || !_onSessionExpired) return;
  _sessionExpiredFired = true;
  _onSessionExpired();
}

function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal })
    .catch((error: Error) => {
      if (error.name === "AbortError") {
        throw new Error(
          `La solicitud excedió el tiempo límite de ${timeoutMs / 1000}s`,
        );
      }
      throw error;
    })
    .finally(() => clearTimeout(timeoutId));
}

function handleHttpError(
  response: Response,
  errorDetail: ParsedApiError,
): never {
  if (response.status === 401) {
    notifySessionExpired();
    throw new ApiRequestError(
      `Error de autenticación: ${errorDetail.message}`,
      response.status,
      errorDetail.code,
      errorDetail.details,
    );
  }
  if (response.status === 403) {
    throw new ApiRequestError(
      `Acceso denegado: ${errorDetail.message}`,
      response.status,
      errorDetail.code,
      errorDetail.details,
    );
  }
  throw new ApiRequestError(
    `Error de API (${response.status}): ${errorDetail.message}`,
    response.status,
    errorDetail.code,
    errorDetail.details,
  );
}

async function parseErrorDetail(response: Response): Promise<ParsedApiError> {
  try {
    const errorData: unknown = await response.json();
    if (typeof errorData !== "object" || errorData === null) {
      return { message: String(errorData) };
    }

    const payload = errorData as Record<string, unknown>;
    return {
      message:
        typeof payload.message === "string"
          ? payload.message
          : typeof payload.error === "string"
            ? payload.error
            : JSON.stringify(payload),
      code: typeof payload.code === "string" ? payload.code : undefined,
      details: payload.details,
    };
  } catch {
    return { message: response.statusText };
  }
}

export async function fetchWithAuth(
  endpoint: string,
  token: string,
  options: RequestInit = {},
) {
  if (!token) {
    throw new Error("Token de autenticación faltante");
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  } as Record<string, string>;

  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetchWithTimeout(url, { ...options, headers });

  if (!response.ok) {
    const errorDetail = await parseErrorDetail(response);
    handleHttpError(response, errorDetail);
  }

  if (options.method === "DELETE" && response.status === 204) {
    return null;
  }

  return await response.json();
}

/** Authenticated private file download, with the same session/error handling. */
export async function fetchBlobWithAuth(
  endpoint: string,
  token: string,
): Promise<Blob> {
  if (!token) throw new Error("Token de autenticación faltante");
  const response = await fetchWithTimeout(
    `${API_BASE_URL}${endpoint}`,
    { headers: { Authorization: `Bearer ${token}` } },
    UPLOAD_TIMEOUT_MS,
  );
  if (!response.ok) handleHttpError(response, await parseErrorDetail(response));
  return response.blob();
}

export async function uploadDriverPicture({
  apiBaseUrl,
  driverId,
  file,
  token,
}: {
  apiBaseUrl: string;
  driverId: number;
  file: File;
  token?: string;
}): Promise<UploadDriverPictureResponse> {
  const form = new FormData();
  form.append("file", file);

  const res = await fetchWithTimeout(
    `${apiBaseUrl}/driver/${driverId}/picture`,
    {
      method: "POST",
      body: form,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
    UPLOAD_TIMEOUT_MS,
  );

  if (!res.ok) {
    const text = await res.text();
    const isHtml = text.trimStart().startsWith("<");
    throw new Error(
      isHtml
        ? `Error al subir la imagen (${res.status})`
        : text || `Error al subir la imagen (${res.status})`,
    );
  }

  return res.json();
}

export async function fetchAbsoluteWithAuth(
  url: string,
  token: string,
  options: RequestInit = {},
): Promise<any> {
  if (!token) {
    throw new Error("Token de autenticación faltante");
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...options.headers,
  } as Record<string, string>;

  const response = await fetchWithTimeout(url, { ...options, headers });

  if (!response.ok) {
    const errorDetail = await parseErrorDetail(response);
    handleHttpError(response, errorDetail);
  }

  if (options.method === "DELETE" && response.status === 204) {
    return null;
  }

  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
}

export function createApiService<T>(
  resourcePath: string,
  getToken: () => Promise<string>,
  customEndpoints?: {
    getAll?: string;
    getById?: string;
    create?: string;
    update?: string;
    delete?: string;
    getMetricsByDriverId?: string;
  },
) {
  return {
    getAll: async (): Promise<T[]> => {
      const token = await getToken();
      const endpoint = customEndpoints?.getAll || resourcePath;
      return fetchWithAuth(endpoint, token);
    },

    getById: async (id: number | string): Promise<T> => {
      const token = await getToken();
      const endpoint = customEndpoints?.getById
        ? `${customEndpoints.getById}/web/${id}`
        : `${resourcePath}/${id}`;
      return fetchWithAuth(endpoint, token);
    },

    create: async (data: Partial<T>): Promise<T> => {
      const token = await getToken();
      const endpoint = customEndpoints?.create || resourcePath;
      return fetchWithAuth(endpoint, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    },

    update: async (id: number | string, data: Partial<T>): Promise<T> => {
      const token = await getToken();
      const endpoint = customEndpoints?.update
        ? `${customEndpoints.update}/${id}`
        : `${resourcePath}/${id}`;
      return fetchWithAuth(endpoint, token, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    },

    delete: async (id: number | string): Promise<void> => {
      const token = await getToken();
      const endpoint = customEndpoints?.delete
        ? `${customEndpoints.delete}/${id}`
        : `${resourcePath}/${id}`;
      return fetchWithAuth(endpoint, token, { method: "DELETE" });
    },

    getMetricsByDriverId: async (id: number | string): Promise<T> => {
      const token = await getToken();
      const endpoint = customEndpoints?.getMetricsByDriverId
        ? `${customEndpoints.getMetricsByDriverId}/${id}`
        : `${resourcePath}/${id}/metrics`;
      return fetchWithAuth(endpoint, token);
    },
  };
}

export async function apiRequest(
  endpoint: string,
  options: RequestInit = {},
): Promise<any> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetchWithTimeout(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorDetail = await parseErrorDetail(response);
    handleHttpError(response, errorDetail);
  }

  return await response.json();
}
